import React, { useEffect, useRef, useState } from "react";
import { typeToString } from "../extensions/helpers";

type DropdownSelectValue = {
  id: string;
  name: string;
  color?: string;
};

type DropdownSelectProps = {
  selected: DropdownSelectValue | null | undefined;
  setSelected: (value: DropdownSelectValue) => void;
  options: DropdownSelectValue[];
  availableOptions?: DropdownSelectValue[];
  disabledOptionExplanation?: string;
  className?: string;
};

function DropdownSelect({
  selected,
  setSelected,
  options,
  availableOptions,
  disabledOptionExplanation,
  className,
}: DropdownSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = (option: DropdownSelectValue) => {
    setSelected(option);
    setIsOpen(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={dropdownRef} className={`relative ${className ?? "w-64"}`}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full border border-gray-300 bg-gray-50 rounded-xl shadow-sm px-4 py-2 text-left text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
        style={{ backgroundColor: selected?.color }}
      >
        {selected?.name || "Select an option"}
        <span className="float-right">
          <svg
            className="w-5 h-5 text-gray-500 inline-block"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 011.08 1.04l-4.25 4.65a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {isOpen && (
        <ul className="absolute mt-1 z-10 w-full bg-white border border-gray-200 rounded-xl shadow-lg">
          {options.map((option, idx) => {
            const isDisabled =
              availableOptions &&
              !availableOptions.map((o) => o.id).includes(option.id);
            const conditionalClassName = isDisabled
              ? "bg-gray-100"
              : "hover:bg-orange-100";

            return (
              <li
                key={idx}
                className={`flex flex-row gap-2 px-4 py-2 transition cursor-pointer ${conditionalClassName} ${
                  option === selected ? "border-black font-medium" : ""
                }`}
                style={{ backgroundColor: option.color }}
                onClick={() => {
                  if (isDisabled) return;
                  handleSelect(option);
                }}
              >
                <label className={`cursor-pointer ${conditionalClassName}`}>
                  {typeToString(option.name)}
                </label>
                {isDisabled && (
                  <label
                    className={`text-red-500 cursor-pointer ${conditionalClassName}`}
                  >
                    {disabledOptionExplanation}
                  </label>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default DropdownSelect;
